import React, { useEffect, useState } from "react";
import { Button, Form, Input, message, Checkbox, Typography } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import IntlMessages from "util/IntlMessages";
import notify from "../Notification";
import { EyeTwoTone, EyeInvisibleOutlined } from "@ant-design/icons";
import {
  hideAuthLoader,
  hideMessage,
  showAuthLoader,
  userSignInSuccess,
  userSignInDataSuccess,
  setInitUrl,
} from "../appRedux/actions/Auth";
import CircularProgress from "../components/CircularProgress";
import AuthService from "../service/AuthService";
import { getPostLoginRedirectUrl } from "../util/AuthRedirect";

const SignIn = () => {
  const dispatch = useDispatch();
  const { loader, alertMessage, showMessage } = useSelector(({ auth }) => auth);
  const history = useHistory();
  const [isLoading, setIsLoading] = useState(false);
  const [checked, setChecked] = useState(false);
  const [locationStatus, setLocationStatus] = useState(""); // "" | "fetching" | "error"
  const [data, setData] = useState({
    email: "",
    password: "",
  });
  const [coords, setCoords] = useState(null);

  const handelChange = (e) => {
    const { name, value } = e.target;
    setData((preValue) => {
      return {
        ...preValue,
        [name]: value?.trim(),
      };
    });
  };

  const onFinishFailed = (errorInfo) => {
    dispatch(hideAuthLoader());
    setIsLoading(false);
  };

  const onChange = () => {
    if (checked === false) {
      setChecked(true);
      const stringifyData = JSON.stringify(data);
      localStorage.setItem("admin-cred", stringifyData);
    } else {
      localStorage.removeItem("admin-cred");
      setChecked(false);
    }
  };

  // ---- NEW: get browser geolocation as a Promise ----
  const getUserLocation = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error("Geolocation is not supported by this browser."));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
        },
        (error) => {
          // error.code: 1 = PERMISSION_DENIED, 2 = POSITION_UNAVAILABLE, 3 = TIMEOUT
          reject(error);
        },
        {
          enableHighAccuracy: false,
          timeout: 60000, //60 seconds wait for user to allow location access
          maximumAge: 60000,
        },
      );
    });
  };

  const onFinish = async (values) => {
    try {
      setIsLoading(true);

      let finalCoords = coords;
      if (!finalCoords) {
        setLocationStatus("fetching");
        try {
          finalCoords = await getUserLocation();
          setCoords(finalCoords);
        } catch (locError) {
          setIsLoading(false);
          dispatch(hideAuthLoader());

          let errMsg =
            "Location access is required to login. Please enable location and try again.";
          if (locError.code === 1) {
            errMsg =
              "Location permission denied. Please allow location access in your browser settings to login.";
          } else if (locError.code === 2) {
            errMsg =
              "Location unavailable. Please check your device's location/GPS settings.";
          } else if (locError.code === 3) {
            errMsg = "Location request timed out. Please try again.";
          }

          notify.openNotificationWithIcon("error", "Location Required", errMsg);
          return;
        }
      }
      dispatch(showAuthLoader());
      if (checked === true) {
        const stringifyData = JSON.stringify(data);
        localStorage.setItem("admin-cred", stringifyData);
      }
      await AuthService.SignIn({
        userId: values.email,
        password: values.password,
        userType: "admin",
        panel: "Admin Panel",
        latitude: finalCoords.latitude,
        longitude: finalCoords.longitude,
      })
        .then((success) => {
          if (success.status === 200) {
            if (success.data?.isOtpRequired) {
              setIsLoading(false);
              dispatch(hideAuthLoader());
              dispatch(hideMessage());
              history.push("/verifyotp", {
                tempToken: success.data.tempToken,
                userId: success.data.userId,
                userType: success.data.userType,
                entityUserType: success.data.entityUserType,
                maskedEmail: success.data.maskedEmail,
              });
              return;
            }

            localStorage.setItem("user_token", success.data.token);
            localStorage.setItem("user", JSON.stringify(success.data));
            notify.openNotificationWithIcon(
              "success",
              "Success",
              "Signin has been successfully done",
            );
            dispatch(userSignInSuccess(success));
            dispatch(userSignInDataSuccess(success.data));
            dispatch(hideMessage());
            setIsLoading(false);
            dispatch(setInitUrl(""));

            const redirectUrl = getPostLoginRedirectUrl(success.data);

            if (redirectUrl) {
              history.replace("/" + redirectUrl);
            } else {
              history.replace("/erropage");
            }
          } else {
            setIsLoading(false);
            dispatch(hideMessage());
            if (success.status !== 422) {
              notify.openNotificationWithIcon(
                "error",
                "Error",
                success.message,
              );
            }
          }
        })
        .catch((err) => {
          dispatch(hideMessage());
          setIsLoading(false);
        });
    } catch (error) {
      dispatch(hideMessage());
      setIsLoading(false);
      notify.openNotificationWithIcon("error", "Error", error);
    }
  };

  useEffect(() => {
    const objectString = localStorage.getItem("admin-cred");
    if (objectString) {
      const data2 = JSON.parse(objectString);
      setData(data2);
      setChecked(true);
    }

    // ---- NEW: fetch location as soon as page loads ----
    getUserLocation()
      .then((loc) => {
        setCoords(loc);
        setLocationStatus("ready");
      })
      .catch((err) => {
        setLocationStatus("error");
        console.log("Location error:", err);
      });
  }, []);

  return (
    <div
      className="gx-app-login-wrap"
      style={{
        backgroundColor: "rgb(4 74 143)",
        backgroundSize: "cover",
      }}
    >
      <div className="gx-app-login-container">
        <div className="gx-app-login-main-content">
          <div style={{ padding: "35px 35px 20px", width: "100%" }}>
            <Typography.Title level={1} style={{ textAlign: "center" }}>
              TES
            </Typography.Title>
            <Form
              fields={[
                { name: ["email"], value: data.email },
                { name: ["password"], value: data.password },
              ]}
              name="basic"
              onFinish={onFinish}
              onFinishFailed={onFinishFailed}
              className="gx-signin-form gx-form-row0"
            >
              <Form.Item
                rules={[
                  {
                    required: true,
                    message: "The input is not valid User Name !",
                  },
                ]}
                name="email"
              >
                <label style={{ fontSize: "20px" }}>User Name</label>
                <Input
                  style={{ marginTop: "10PX" }}
                  placeholder="User Name"
                  name="email"
                  onChange={handelChange}
                  value={data?.email}
                />
              </Form.Item>
              <Form.Item
                rules={[
                  { required: true, message: "Please input your Password!" },
                ]}
                name="password"
              >
                <label style={{ fontSize: "20px" }}>Password</label>
                <Input.Password
                  style={{ marginTop: "10PX" }}
                  type="password"
                  placeholder="Password"
                  value={data?.password}
                  name="password"
                  onChange={handelChange}
                  iconRender={(visible) =>
                    visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
                  }
                />
              </Form.Item>

              <Form.Item>
                <div className="gx-form-row0">
                  <Checkbox onChange={onChange} checked={checked} />
                  <span> Remember Me</span>
                </div>
                {locationStatus === "fetching" && (
                  <div style={{ color: "#fff", marginBottom: "8px" }}>
                    Fetching your location, please allow permission...
                  </div>
                )}
                <Button
                  loading={isLoading}
                  block
                  type="primary"
                  className="gx-mb-0 gx-mt-2"
                  style={{
                    backgroundColor: "rgb(4 74 143)",
                    color: "white",
                    fontSize: "20px",
                    height: "50px",
                  }}
                  htmlType="submit"
                >
                  <IntlMessages id="app.userAuth.signIn" />
                </Button>
              </Form.Item>
            </Form>
          </div>
          {loader ? (
            <div className="gx-loader-view">
              <CircularProgress />
            </div>
          ) : null}
          {showMessage ? message.error(alertMessage.toString()) : null}
        </div>
      </div>
    </div>
  );
};

export default SignIn;
